import { forwardRef } from 'react';
import { useController, useFormContext } from 'react-hook-form';

import type { IconDefinition } from '@fortawesome/fontawesome-common-types';

import { TextAreaField, type TextAreaFieldProps } from '../../components/TextAreaField';

export interface RHFTextAreaFieldProps {
    name: string;
    label: TextAreaFieldProps['label'];
    defaultValue?: TextAreaFieldProps['defaultValue'];
    placeholder?: TextAreaFieldProps['placeholder'];
    description?: TextAreaFieldProps['description'];
    rows?: TextAreaFieldProps['rows'];
    dataTestid?: TextAreaFieldProps['dataTestid'];
    labelHtmlAttributes?: TextAreaFieldProps['labelHtmlAttributes'];
    inputHtmlAttributes?: TextAreaFieldProps['inputHtmlAttributes'];
    required?: TextAreaFieldProps['required'];
    disabled?: TextAreaFieldProps['disabled'];
    className?: TextAreaFieldProps['className'];
    icon?: IconDefinition;
}

export const RHFTextAreaField = forwardRef<HTMLTextAreaElement, RHFTextAreaFieldProps>(
    (
        {
            name,
            label,
            defaultValue,
            className,
            placeholder,
            description,
            rows,
            dataTestid,
            labelHtmlAttributes,
            inputHtmlAttributes,
            required,
            disabled,
            icon,
        },
        ref
    ) => {
        const { control } = useFormContext();
        const { field, fieldState } = useController({
            control,
            name,
            defaultValue,
        });

        return (
            <TextAreaField
                id={field.name}
                name={field.name}
                value={field.value ?? ''}
                label={label}
                defaultValue={field.value}
                placeholder={placeholder}
                description={description}
                rows={rows}
                className={className}
                dataTestid={dataTestid}
                labelHtmlAttributes={labelHtmlAttributes}
                inputHtmlAttributes={inputHtmlAttributes}
                required={required}
                disabled={disabled}
                icon={icon}
                onChange={field.onChange}
                onBlur={field.onBlur}
                inputRef={(element) => {
                    field.ref(element);
                    if (ref) {
                        if (typeof ref === 'function') {
                            ref(element);
                        } else {
                            ref.current = element;
                        }
                    }
                }}
                errorMessage={fieldState.error?.message}
            />
        );
    }
);

RHFTextAreaField.displayName = 'RHFTextAreaField';
